import React, { useState, useMemo } from 'react';
import Select from '@vromo/ui/components/Select2';
import { observer } from 'mobx-react-lite';

import { useStore } from '@vromo/mobx-react';
import Button from '@vromo/ui/components/Button';
import Modal, { ModalHeader, ModalActions, ModalContent } from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import { dataTestId, DispatchStatus, Job, Vehicle } from '@vromo/models';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { useDispatchPage } from '../../helpers';
import { LabelValue } from '@vromo/models/helpers/models';
import { FilterRow } from '@vromo/dashboard/src/blueprints/BlueprintStyles';
import OverlayablePanel from '@vromo/ui/components/Overlay';
import SelectDriver from './SelectDriver';
import { qaLabelDispatch } from './../../../../../../common-qa-labels/dispatch';

type DispatchType = "dispatch" | "offer"
const dispatchOptions: LabelValue<string, DispatchType>[] = [
    {
        label: "Dispatch",
        value: "dispatch",
    },
    {
        label: "Offer",
        value: "offer"
    }
]


interface DispatchModalProps {
    isOpen: boolean;
    jobs: Job[];
    close: () => void;
    types?: DispatchStatus[];
}

/** Assign job modal */
const DispatchModal = observer<DispatchModalProps>(({
    isOpen,
    jobs,
    close,
    types = ['dispatched']
}) => {

    const { vehicleStore: { filteredVehicles } } = useStore<RootStore>('rootStore');
    const { dispatchJobs, offerJobs, isLoading } = useDispatchPage()
    const [dispatchType, setDispatchType] = useState<DispatchType>("dispatch")
    const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([])
    const allowOffer = useMemo(() => jobs.filter(({ dispatched }) => dispatched).length < 1, [jobs])

    const CloseButton = () => <Button onClick={close} disabled={isLoading}> Cancel </Button>


    if (filteredVehicles.length < 1)
        return (
            <Modal isOpen={isOpen} onRequestClose={close}>
                <ModalHeader>
                    <Title>Assign job</Title>
                </ModalHeader>
                <ModalContent>
                    <p>
                        No drivers match your filter criteria.<br />
                        Try clearing any filters applied to the
                        driver list before dispatching.
                    </p>
                </ModalContent>
                <ModalActions>
                    <CloseButton />
                </ModalActions>
            </Modal>
        )


    return (
        <Modal isOpen={isOpen} onRequestClose={close}>
            <OverlayablePanel active={isLoading} >
                <ModalHeader>
                    <Title data-testid={qaLabelDispatch.assignJobModal.title}>Assign job</Title>
                </ModalHeader>
                {!allowOffer &&
                    <div style={{ textAlign: 'left', fontStyle: 'italic' }}>
                        <span>{`* Some selected jobs are already assigned`}</span>
                    </div>
                }
                <ModalContent limitWidth>
                    <FilterRow id="dispatch-mode-select" data-testid={qaLabelDispatch.assignJobModal.dispatchDropdownDiv}>
                        <Select
                            id ='assign-mode-select'
                            styles={{
                                container: (styles) => ({
                                    ...styles,
                                    marginRight: '10px',
                                    flex: 2
                                })
                            }}
                            isDisabled={!allowOffer}
                            value={dispatchOptions.filter(option => option.value === dispatchType)!}
                            onChange={(e) => setDispatchType(e!.value)}
                            options={dispatchOptions}
                            clearable={false}
                            instanceId={'dispatch-mode-select'}
                        />
                        <label style={{
                            marginLeft: '10px',
                            flex: 3
                        }}>
                            {`${jobs.length} job${jobs.length > 1 ? "s" : ""} to...`}
                        </label>

                    </FilterRow>

                    <SelectDriver
                        jobs={jobs}
                        onSelect={setSelectedVehicles}
                        offer={dispatchType === "offer"}
                    />
                    {dispatchType === "offer" &&
                        <div style={{ textAlign: 'right' }}>
                            <label>{`${selectedVehicles.length} driver${selectedVehicles.length != 1 ? "s" : ""} selected`}</label>
                        </div>
                    }
                </ModalContent>
                <ModalActions>
                    <CloseButton />
                    <Button
                        data-testid={qaLabelDispatch.assignJobModal.assignJobBtn}
                        disabled={selectedVehicles.length < 1 || isLoading}
                        onClick={async () => {
                            dispatchType === "offer" ?
                                await offerJobs(jobs, selectedVehicles) :
                                await dispatchJobs(jobs, types, selectedVehicles[0]!)
                            close()
                        }}>
                        <span style={{
                            textTransform: 'capitalize'
                        }}>
                            Assign
                        </span>
                        {` job${jobs.length > 1 ? "s" : ""}`}
                    </Button>
                </ModalActions>
            </OverlayablePanel>
        </Modal>
    );
});


export default DispatchModal
